import COS from 'cos-js-sdk-v5';

const API_BASE = '/domi-api';
const TOKEN_KEY = 'domi_token';
const PROJECT_KEY_STORAGE_KEY = 'domi_scratch_project_key';

const readSessionStorage = key => {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeSessionStorage = (key, value) => {
    try {
        window.sessionStorage.setItem(key, value);
    } catch {
        // Saving still works in browsers which disable sessionStorage.
    }
};

const removeSessionStorage = key => {
    try {
        window.sessionStorage.removeItem(key);
    } catch {
        // There is no persisted project key to remove.
    }
};

const createId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID().replace(/-/g, '');
    }
    const randomPart = Math.random().toString(36)
        .slice(2);
    return `${Date.now().toString(36)}${randomPart}`
        .padEnd(32, '0')
        .slice(0, 32);
};

const getLoginToken = () => {
    try {
        return window.localStorage.getItem(TOKEN_KEY) || '';
    } catch {
        return '';
    }
};

const hasLoginToken = () => Boolean(getLoginToken());

const requireLoginToken = () => {
    const token = getLoginToken();
    if (!token) throw new Error('请先登录 Domi 账号');
    return token;
};

const request = async (path, options = {}) => {
    const token = requireLoginToken();
    const response = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
            Authorization: token,
            ...(options.body ? {'Content-Type': 'application/json'} : {}),
            ...options.headers
        }
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || (typeof result.code !== 'undefined' && result.code !== 200)) {
        throw new Error(result.msg || `请求失败 (${response.status})`);
    }
    return result.data;
};

const parseCosPrefix = rawPrefix => {
    if (!rawPrefix) return '';
    return rawPrefix.split(',')[0].trim()
        .replace(/\*+$/, '')
        .replace(/^\/+|\/+$/g, '');
};

const getCloudProjectKey = () => readSessionStorage(PROJECT_KEY_STORAGE_KEY);

const setCloudProjectKey = projectKey => writeSessionStorage(PROJECT_KEY_STORAGE_KEY, projectKey);

const clearCloudProjectKey = () => removeSessionStorage(PROJECT_KEY_STORAGE_KEY);

const ensureCloudProjectKey = () => {
    const current = getCloudProjectKey();
    if (current) return current;
    const projectKey = createId();
    setCloudProjectKey(projectKey);
    return projectKey;
};

const getCosCredential = () => request('/v1/oss/anon/sts/cos');

const uploadProject = async (content, projectKey) => {
    const sts = await getCosCredential();
    const prefix = parseCosPrefix(sts.allowPrefixes);
    if (!prefix) throw new Error('COS 上传目录无效');

    const fileKey = `${prefix}/scratch/${projectKey}/latest.sb3`;
    const cos = new COS({
        getAuthorization: (_options, callback) => callback({
            TmpSecretId: sts.credentials.tmpSecretId,
            TmpSecretKey: sts.credentials.tmpSecretKey,
            SecurityToken: sts.credentials.sessionToken,
            StartTime: sts.startTime,
            ExpiredTime: sts.expiredTime
        })
    });

    await new Promise((resolve, reject) => {
        cos.uploadFile({
            Bucket: sts.bucketName,
            Region: sts.region,
            Key: fileKey,
            Body: content
        }, (error, data) => {
            if (error) {
                reject(new Error(error.message || error.error?.Message || 'COS 上传失败'));
                return;
            }
            resolve(data);
        });
    });
    return fileKey;
};

const saveCloudProject = async (content, projectName) => {
    const projectKey = ensureCloudProjectKey();
    const fileKey = await uploadProject(content, projectKey);
    return request('/v1/scratch/projects', {
        method: 'POST',
        body: JSON.stringify({
            projectKey,
            projectName,
            fileKey,
            fileSize: content.size
        })
    });
};

const listCloudProjects = async () => {
    const params = new URLSearchParams({size: '50'});
    return request(`/v1/scratch/projects?${params.toString()}`);
};

export {
    clearCloudProjectKey,
    ensureCloudProjectKey,
    getCloudProjectKey,
    hasLoginToken,
    listCloudProjects,
    parseCosPrefix,
    saveCloudProject,
    setCloudProjectKey
};
